import { useState } from 'react';
import { Check } from 'lucide-react';
import type { InductionAttemptResult, InductionToTake } from '@cloudsign/shared';
import { QuizRunner, VideoPlayer } from '../../components/phase3';
import { t } from '../../lib/i18n';
import { Alert, Button, CheckboxField } from '../../components/ui';
import { problemText } from './api';
import { newKey } from './util';

/** Sends one attempt to wherever the page keeps it (contactless session or pre-arrival link). */
export interface Submit {
  (a: AttemptInput): Promise<InductionAttemptResult>;
}

export interface AttemptInput {
  idempotencyKey: string;
  inductionId: string;
  version: number;
  answers: Record<string, string[]>;
  watchedSeconds: number;
  occurredAt: string;
}

/**
 * One induction: read or watch the material, then answer the quiz. Used on the contactless page and the pre-arrival page,
 * which differ only in where the attempt is sent. The server grades; correct answers never reach the phone.
 */
function OneInduction({ item, submit, onPassed }: { item: InductionToTake; submit: Submit; onPassed: (until: string | null) => void }) {
  const [watched, setWatched] = useState(0);
  const [ready, setReady] = useState(item.media?.kind !== 'video' && item.minWatchSeconds === 0);
  const [read, setRead] = useState(false);
  const [result, setResult] = useState<InductionAttemptResult | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [round, setRound] = useState(0);
  const [key, setKey] = useState(newKey);

  const send = async (answers: Record<string, string[]>) => {
    setBusy(true);
    setProblem(null);
    try {
      const r = await submit({ idempotencyKey: key, inductionId: item.inductionId, version: item.version, answers, watchedSeconds: Math.floor(watched), occurredAt: new Date().toISOString() });
      setResult(r);
      setKey(newKey());
      if (r.passed) onPassed(r.passedUntil);
    } catch (e) {
      setProblem(problemText(e));
    } finally {
      setBusy(false);
    }
  };

  // The server tells a phone only the score, not which questions were wrong.
  const failed = result !== null && !result.passed;
  const wrongCount = failed ? result.wrong.length : 0;
  return (
    <div className="space-y-3 rounded-md border border-slate-200 p-3">
      <h3 className="text-lg font-semibold">{item.name}</h3>
      {item.body ? <p className="whitespace-pre-line text-base text-slate-800">{item.body}</p> : null}
      {item.media?.kind === 'video' ? (
        <VideoPlayer
          src={item.media.url}
          title={item.name}
          minWatchSeconds={item.minWatchSeconds}
          onWatched={setWatched}
          onComplete={() => setReady(true)}
        />
      ) : null}
      {item.media?.kind === 'pdf' ? (
        <a href={item.media.url} target="_blank" rel="noreferrer noopener" referrerPolicy="no-referrer" className="inline-flex min-h-12 items-center text-base text-brand underline">
          {t('w21.induction.openDocument')}
        </a>
      ) : null}
      {item.media?.kind !== 'video' && (item.media || item.body) && !ready ? (
        <CheckboxField label={t('w21.induction.haveRead')} checked={read} onChange={(e) => { setRead(e.target.checked); setReady(e.target.checked); }} />
      ) : null}
      {item.media?.kind === 'video' && !ready ? <p className="text-sm text-slate-700">{t('w21.induction.watchFirst', { seconds: item.minWatchSeconds })}</p> : null}

      {ready && !result?.passed ? (
        item.questions.length > 0 ? (
          <div className="space-y-2">
            <h4 className="font-medium">{t('w21.induction.quizTitle', { pass: item.passMarkPercent })}</h4>
            {wrongCount > 0 ? (
              <Alert>{t('w21.induction.tryAgain', { score: result?.scorePercent ?? 0, wrong: wrongCount })}</Alert>
            ) : failed ? (
              <Alert>{t('w21.induction.tryAgainScore', { score: result?.scorePercent ?? 0 })}</Alert>
            ) : null}
            <QuizRunner key={round} questions={item.questions} passMarkPercent={item.passMarkPercent} disabled={busy} onSubmit={(a) => { setRound((n) => n + 1); void send(a); }} />
          </div>
        ) : (
          <Button variant="primary" className="min-h-12 w-full text-base" disabled={busy} onClick={() => void send({})}>
            {t('w21.induction.complete')}
          </Button>
        )
      ) : null}
      {problem ? <Alert>{problem}</Alert> : null}
      {result?.passed ? (
        <p className="flex items-center gap-2 font-medium text-green-800">
          <Check aria-hidden="true" className="h-5 w-5" />
          {t('w21.induction.passed', { score: result.scorePercent })}
        </p>
      ) : null}
    </div>
  );
}

/** Every induction the visitor still has to take, one card each. Calls onAllPassed when the last one is passed. */
export function InductionTaker({ items, submit, onAllPassed }: { items: InductionToTake[]; submit: Submit; onAllPassed?: () => void }) {
  const [passed, setPassed] = useState<Set<string>>(new Set());
  return (
    <div className="space-y-3">
      {items.map((i) => (
        <OneInduction
          key={`${i.inductionId}-${i.version}`}
          item={i}
          submit={submit}
          onPassed={() => {
            const next = new Set(passed).add(i.inductionId);
            setPassed(next);
            if (items.every((x) => next.has(x.inductionId))) onAllPassed?.();
          }}
        />
      ))}
    </div>
  );
}
